import React, { useMemo } from 'react';
import { Platform, Pressable, View, Text } from 'react-native';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import apis from '@api/api';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import {
  MODAL_CONTENT_Z_INDEX,
  MODAL_OVERLAY_Z_INDEX,
  MODAL_PORTAL_Z_INDEX,
} from '@components/modals/shared/adaptiveModalTheme';

export interface TrialDeclineFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  /** Persist "already asked" for this trial period */
  onComplete: () => void;
}

/**
 * Shown after the user taps "Remind me later" on the last trial reminder (1 day left),
 * once they have been in trial for at least 7 days. Uses GenerateSchemaForm; server may
 * return a promo code when feedback meets quality thresholds.
 */
const TrialDeclineFeedbackModal: React.FC<TrialDeclineFeedbackModalProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const theme = themeSelector();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const titleFs = responsiveFontSize(18);
  const bodyFs = responsiveFontSize(14);

  const feedbackSchema = useMemo(
    () => ({
      rating: {
        title: 'rating',
        key: 'rating',
        type: 'dropDown' as const,
        required: true,
        content: {
          type: 'dropDown' as const,
          label: t('trialDeclineFieldRating'),
          enum: [
            { label: `5 — ${t('trialRatingExcellent')}`, value: 5 },
            { label: `4 — ${t('trialRatingGood')}`, value: 4 },
            { label: `3 — ${t('trialRatingOk')}`, value: 3 },
            { label: `2 — ${t('trialRatingPoor')}`, value: 2 },
            { label: `1 — ${t('trialRatingVeryPoor')}`, value: 1 },
          ],
          validation: { required: true },
        },
      },
      primary_reason: {
        title: 'primary_reason',
        key: 'primary_reason',
        type: 'dropDown' as const,
        required: true,
        content: {
          type: 'dropDown' as const,
          label: t('trialDeclineFieldReason'),
          enum: [
            { label: t('trialDeclineReasonPrice'), value: 'price' },
            { label: t('trialDeclineReasonFeatures'), value: 'features' },
            { label: t('trialDeclineReasonTiming'), value: 'timing' },
            { label: t('trialDeclineReasonOther'), value: 'other' },
          ],
          validation: { required: true },
        },
      },
      main_feedback: {
        title: 'main_feedback',
        key: 'main_feedback',
        type: 'string' as const,
        required: true,
        content: {
          label: t('trialDeclineFieldDetails'),
          validation: {
            required: true,
            minCharacters: 40,
            maxCharacters: 4000,
          },
        },
      },
    }),
    [t]
  );

  return (
    <Dialog open={open}>
      <Dialog.Portal zIndex={MODAL_PORTAL_Z_INDEX}>
        <Dialog.Overlay
          animation="quick"
          opacity={0.55}
          style={{
            zIndex: MODAL_OVERLAY_Z_INDEX,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)' }),
          }}
        />
        <Dialog.Content
          bordered
          elevate
          style={{
            padding: 0,
            zIndex: MODAL_CONTENT_Z_INDEX,
            maxWidth: 560,
            width: Platform.OS === 'web' ? 560 : '92%',
            maxHeight: Platform.OS === 'web' ? '82vh' : '88%',
            borderRadius: 20,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
          }}
        >
          <View
            style={{
              backgroundColor: theme ? '#f0fdf4' : '#14532d',
              paddingHorizontal: 20,
              paddingVertical: 16,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#bbf7d0' : '#166534',
            }}
          >
            <XStack alignItems="center" gap="$3">
              <MaterialIcons
                name="card-giftcard"
                size={22}
                color={theme ? '#15803d' : '#86efac'}
              />
              <YStack flex={1}>
                <Text
                  style={{
                    fontSize: titleFs,
                    fontWeight: '700',
                    color: theme ? '#14532d' : '#dcfce7',
                  }}
                >
                  {t('trialDeclineFeedbackTitle')}
                </Text>
                <Text
                  style={{
                    fontSize: bodyFs,
                    color: theme ? '#166534' : '#bbf7d0',
                    marginTop: 4,
                  }}
                >
                  {t('trialDeclineFeedbackSubtitle')}
                </Text>
              </YStack>
              <Pressable
                onPress={() => {
                  onComplete();
                  onClose();
                }}
                hitSlop={12}
              >
                <MaterialIcons name="close" size={22} color={theme ? '#166534' : '#bbf7d0'} />
              </Pressable>
            </XStack>
          </View>

          <View style={{ paddingHorizontal: 12, paddingBottom: 16, maxHeight: 520 }}>
            {open ? (
            <GenerateSchemaForm
              schema={feedbackSchema}
              submitFunction={async (values: Record<string, unknown>) => {
                const res = await apis.postFeedback({
                  ...values,
                  source: 'trial_decline',
                  page_details: 'Trial expiration / Upgrade',
                });
                if (res?.status && res.status >= 400) {
                  throw new Error('submit failed');
                }
                onComplete();
                return res;
              }}
              extraCallBack={(data: { promoCode?: string; promoDescription?: string }) => {
                if (data?.promoCode) {
                  dispatch({
                    type: SHOW_AUTH_MODAL,
                    data: {
                      content: t('trialDeclinePromoGranted', {
                        code: data.promoCode,
                        desc: data.promoDescription || t('trialDeclinePromoDefaultDesc'),
                      }),
                      type: 'success',
                    },
                  });
                } else {
                  dispatch({
                    type: SHOW_AUTH_MODAL,
                    data: {
                      content: t('trialDeclineFeedbackThanks'),
                      type: 'success',
                    },
                  });
                }
                onClose();
              }}
              showSkipButton
              skipButtonLabel={t('trialDeclineSkipFeedback')}
              onSkip={() => {
                onComplete();
                onClose();
              }}
              buttonLabel={t('trialDeclineSubmitFeedback')}
              Progress={false}
              addPadding
              scrollEnabled
            />
            ) : null}
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default TrialDeclineFeedbackModal;
